import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { PageHeader, StatusBadge, Icon, DueBadge, isCarIcon, CarIconBadge } from '@carrent/ui'
import { api, regionsApi } from '@carrent/api'
import { describeCarDue } from '@carrent/domain'
import { useAuthStore } from '@carrent/core'
import { RegionPicker, RegionBadge } from '../../components/RegionPicker'
import { DataTable } from '../../components/DataTable'
import { Drawer } from '../../components/Drawer'
import { FleetMap } from '../../components/fleetMap/FleetMap'
import { CarPreview } from './CarPreview'

/**
 * Реестр автопарка.
 *
 * Страница отвечает не на вопрос «какие машины существуют» — на него
 * отвечает справочник, — а на вопрос «что сейчас происходит с парком
 * и где требуется моё действие».
 *
 * Отсюда её устройство. Сигналы вынесены в отдельный ряд и раскрашены:
 * раньше «Ремонт» и «Доступны» выглядели одинаковыми кнопками, хотя
 * первое означает проблему, а второе просто срез. Статус стал селектором,
 * потому что срезом он и является. Сигналы считает сервер по всему парку:
 * счётчик, посчитанный по текущей странице, врал бы при пагинации.
 */

const STATUS_OPTIONS = [
  { value: '', label: 'Все статусы' },
  { value: 'available', label: 'Доступны' },
  { value: 'in_use', label: 'В поездке' },
  { value: 'maintenance', label: 'На ТО' },
  { value: 'repair', label: 'В ремонте' },
  { value: 'inspection_required', label: 'Нужен осмотр' },
  { value: 'blocked', label: 'Заблокированы' },
]

/** Сигналы. Порядок — по тяжести: сверху то, с чем разбираются первым. */
const SIGNALS = [
  { key: 'overdue', label: 'Просрочен срок', icon: 'warning', tone: 'danger' },
  { key: 'no_signal', label: 'Нет данных', icon: 'map', tone: 'warning' },
  { key: 'long_repair', label: 'Долгий ремонт', icon: 'wrench', tone: 'warning' },
  { key: 'due_soon', label: 'Срок на подходе', icon: 'clock', tone: 'info' },
]

const PER_PAGE = 50

const EMPTY = {
  brand: '', model: '', plate: '', vin: '',
  year: new Date().getFullYear(), color: '', mileage: 0, comment: '',
  region_id: null,
}

// ── Колонки ──────────────────────────────────────────────────────────────────

const CAR_COLUMNS = [
  {
    key: 'car',
    label: 'Автомобиль',
    sortable: true,
    sortValue: c => c.plate,
    render: c => (
      <div className="car-cell">
        {isCarIcon(c.photo_main) ? (
          <span
            className="car-cell__icon"
            style={{ background: c.photo_main.split(':')[2] || 'var(--secondary)' }}
          >
            <CarIconBadge src={c.photo_main} size={22} style={{ borderRadius: 0, background: 'transparent' }} />
          </span>
        ) : (
          <span className="car-cell__icon car-cell__icon--empty">
            <Icon name="car" size={18} />
          </span>
        )}
        <span className="car-cell__id">
          {/* Госномер первым: машину ищут по номеру, а не по бренду. */}
          <span className="table-plate">{c.plate}</span>
          <span className="car-cell__model">
            {c.brand} {c.model}
            {c.body_type && c.body_type !== 'other' && c.body_type_label
              ? ` · ${c.body_type_label}`
              : ''}
          </span>
        </span>
      </div>
    ),
  },
  {
    key: 'driver',
    label: 'Водитель / состояние',
    sortValue: c => c.ops?.driver?.full_name || null,
    render: c => <DriverCell car={c} />,
  },
  {
    key: 'due',
    label: 'Ближайший срок',
    sortable: true,
    sortValue: c => (c.ops?.due ? c.ops.due.days_left : null),
    render: c => {
      const due = describeCarDue(c.ops?.due)
      return due ? <DueBadge due={due} compact /> : <span className="table__muted">—</span>
    },
  },
  {
    key: 'region',
    label: 'Регион',
    width: 'assignee',
    sortValue: c => c.region?.code || null,
    render: c => (c.region ? <RegionBadge region={c.region} /> : <span className="table__muted">—</span>),
  },
  {
    key: 'status',
    label: 'Статус',
    width: 'status',
    render: c => <StatusBadge status={c.status} />,
  },
  {
    key: 'mileage',
    label: 'Пробег',
    sortable: true,
    cellClassName: 'table__muted',
    render: c => (c.mileage ? `${c.mileage.toLocaleString('ru')} км` : '—'),
  },
]

function DriverCell({ car }) {
  const ops = car.ops
  if (!ops) return <span className="table__muted">—</span>

  if (ops.driver) {
    return (
      <div className="driver-cell">
        <span>{ops.driver.full_name}</span>
        <SeenNote at={ops.last_seen_at} />
      </div>
    )
  }

  return (
    <div className="driver-cell">
      <span className="table__muted">Свободна</span>
      {ops.open_repairs > 0 && (
        <span className="driver-cell__note driver-cell__note--warning">
          <Icon name="wrench" size={12} />
          {ops.open_repairs === 1 ? 'Открыт ремонт' : `Открыто ремонтов: ${ops.open_repairs}`}
        </span>
      )}
    </div>
  )
}

/**
 * Время последней точки трека.
 *
 * Формулировка намеренно описательная: «данные 3 ч назад», а не «водитель
 * не выходит на связь». Трек заведомо дырявый — браузер глушит геолокацию,
 * когда экран гаснет, — и молчание не доказывает нарушения. Вывод из факта
 * делает человек.
 */
function SeenNote({ at }) {
  if (!at) {
    return (
      <span className="driver-cell__note driver-cell__note--warning">
        <Icon name="map" size={12} />
        Данных нет
      </span>
    )
  }

  const hours = Math.floor((Date.now() - new Date(at).getTime()) / 3600000)

  if (hours < 1) {
    return (
      <span className="driver-cell__note driver-cell__note--ok">
        <Icon name="map" size={12} />
        На линии
      </span>
    )
  }

  const stale = hours >= 24
  return (
    <span className={`driver-cell__note ${stale ? 'driver-cell__note--warning' : ''}`.trim()}>
      <Icon name="map" size={12} />
      {stale ? `Данные ${Math.floor(hours / 24)} дн. назад` : `Данные ${hours} ч назад`}
    </span>
  )
}

// ── Страница ─────────────────────────────────────────────────────────────────

export default function AdminCarsPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [cars, setCars] = useState([])
  const [counts, setCounts] = useState({})
  const [attention, setAttention] = useState({})
  const [meta, setMeta] = useState({ page: 1, pages: 1, total: 0 })

  const [status, setStatus] = useState('')
  const [signal, setSignal] = useState('')
  const [search, setSearch] = useState('')
  const [regionFilter, setRegionFilter] = useState('')
  const [page, setPage] = useState(1)
  const [view, setView] = useState('table')
  const [selected, setSelected] = useState(null)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState(EMPTY)
  const [saving, setSaving] = useState(false)
  const [formErr, setFormErr] = useState('')

  useEffect(() => {
    // Гаджет «Загрузка автопарка» ведёт сюда с готовым срезом:
    // без этого клик по сегменту открывал бы весь список.
    const s = searchParams.get('status')
    if (s !== null) setStatus(s)

    const rid = searchParams.get('region_id')
    if (rid) {
      setForm(f => ({ ...f, region_id: Number(rid) }))
      setShowForm(true)
    }
  }, [searchParams])

  const load = () => {
    setLoading(true)
    setError('')
    api
      .get('/admin/cars', {
        params: {
          status: status || undefined,
          attention: signal || undefined,
          q: search || undefined,
          region_id: regionFilter || undefined,
          with: 'ops',
          page,
          per_page: PER_PAGE,
        },
      })
      .then(r => {
        const data = r.data?.data || {}
        setCars(data.items || [])
        setCounts(data.counts || {})
        setAttention(data.attention || {})
        setMeta(data.meta || { page: 1, pages: 1, total: 0 })
      })
      .catch(() => setError('Не удалось загрузить список машин'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    const t = setTimeout(load, search ? 350 : 0)
    return () => clearTimeout(t)
  }, [status, signal, search, regionFilter, page])

  // Смена среза возвращает на первую страницу: иначе фильтр, сузивший
  // выдачу до одной страницы, оставил бы пользователя на пустой седьмой.
  const changeSlice = setter => value => {
    setPage(1)
    setter(value)
  }

  const toggleSignal = key => {
    setPage(1)
    setSignal(s => (s === key ? '' : key))
  }

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }))

  const handleCreate = async e => {
    e.preventDefault()
    setFormErr('')
    if (!form.brand || !form.model || !form.plate) {
      setFormErr('Марка, модель и госномер обязательны')
      return
    }
    setSaving(true)
    try {
      const { data } = await api.post('/admin/cars', form)
      setShowForm(false)
      setForm(EMPTY)
      navigate(`/admin/cars/${data.data.id}`)
    } catch (err) {
      setFormErr(err.response?.data?.message || 'Ошибка')
    } finally {
      setSaving(false)
    }
  }

  const me = useAuthStore(st => st.user)
  const myRegions = (me?.regions || []).map(r => (typeof r === 'object' ? r.id : r))
  // Правило то же, что в can_access_car на сервере. Решает сервер,
  // интерфейс лишь не предлагает недоступного.
  const canEditCar = car =>
    me?.role === 'admin'
      ? myRegions.length === 0 || myRegions.includes(car?.region_id)
      : me?.role === 'manager' && myRegions.includes(car?.region_id)

  // Сохранение из панели обновляет и строку списка, и саму панель:
  // иначе диспетчер видит старое значение там, откуда только что правил.
  const handleSaved = updated => {
    if (!updated) return
    setSelected(prev => (prev ? { ...prev, ...updated, ops: prev.ops } : prev))
    setCars(list => list.map(c => (c.id === updated.id ? { ...c, ...updated, ops: c.ops } : c)))
  }

  const sliced = Boolean(status || signal || search || regionFilter)
  const total = meta.total ?? cars.length

  return (
    <div>
      <PageHeader
        title="Автопарк"
        right={
          <button className="btn btn--primary btn--sm" onClick={() => setShowForm(s => !s)}>
            {showForm ? 'Закрыть' : 'Добавить машину'}
          </button>
        }
      />

      {showForm && (
        <div className="car-form">
          <p className="car-form__title">Новый автомобиль</p>
          {formErr && <div className="alert alert--error">{formErr}</div>}
          <form onSubmit={handleCreate} className="flex-col gap-sm">
            <div className="car-form__grid">
              {[
                { k: 'brand', ph: 'Марка *', req: true },
                { k: 'model', ph: 'Модель *', req: true },
                { k: 'plate', ph: 'Госномер *', req: true },
                { k: 'vin', ph: 'VIN' },
                { k: 'year', ph: 'Год', t: 'number' },
                { k: 'color', ph: 'Цвет' },
              ].map(({ k, ph, t, req }) => (
                <input key={k} className="input" placeholder={ph} type={t || 'text'}
                  value={form[k]} onChange={set(k)} required={!!req} />
              ))}
            </div>
            <input className="input" placeholder="Начальный пробег (км)" type="number"
              value={form.mileage} onChange={set('mileage')} />
            <input className="input" placeholder="Комментарий (необязательно)"
              value={form.comment} onChange={set('comment')} />
            <RegionPicker value={form.region_id} onChange={v => setForm(f => ({ ...f, region_id: v }))} />
            <button className="btn btn--primary btn--full" type="submit" disabled={saving}>
              {saving ? <span className="spinner" /> : 'Создать и открыть'}
            </button>
          </form>
        </div>
      )}

      <div className="fleet">
        <AttentionBar counts={attention} active={signal} onToggle={toggleSignal} />

        <div className="fleet__filters">
          <label className="fleet__field">
            <span className="fleet__field-label">Статус</span>
            <select className="input" value={status} onChange={e => changeSlice(setStatus)(e.target.value)}>
              {STATUS_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                  {o.value && counts[o.value] != null ? ` (${counts[o.value]})` : ''}
                </option>
              ))}
            </select>
          </label>

          <RegionFilter value={regionFilter} onChange={changeSlice(setRegionFilter)} />

          <label className="fleet__field fleet__field--search">
            <span className="fleet__field-label">Поиск</span>
            <span className="fleet__search">
              <Icon name="search" size={16} className="fleet__search-icon" />
              <input
                className="input fleet__search-input"
                placeholder="Госномер, марка или модель"
                value={search}
                onChange={e => changeSlice(setSearch)(e.target.value)}
              />
              {search && (
                <button type="button" className="fleet__search-clear" aria-label="Очистить поиск"
                  onClick={() => changeSlice(setSearch)('')}>
                  <Icon name="close" size={14} />
                </button>
              )}
            </span>
          </label>

          {!loading && (
            <span className="fleet__total">{total} {carWord(total)}</span>
          )}

          <ViewSwitch value={view} onChange={setView} />
        </div>

        {view === 'table' ? (
          <>
            <DataTable
              columns={CAR_COLUMNS}
              rows={cars}
              loading={loading}
              error={error}
              onRetry={load}
              onRowClick={setSelected}
              empty={{
                icon: 'car',
                title: sliced ? 'В этом срезе машин нет' : 'Машин пока нет',
                text: sliced
                  ? 'Измените статус, сигнал или поисковый запрос.'
                  : 'Добавьте первый автомобиль в парк.',
              }}
            />
            <Pagination meta={meta} onChange={setPage} />
          </>
        ) : (
          <FleetMap cars={cars} onSelect={setSelected} />
        )}

        <Drawer
          open={Boolean(selected)}
          title={selected ? selected.plate : ''}
          subtitle={selected ? `${selected.brand} ${selected.model}` : ''}
          onClose={() => setSelected(null)}
          footer={
            selected && (
              <button
                type="button"
                className="btn btn--primary btn--full"
                onClick={() => navigate(`/admin/cars/${selected.id}`)}
              >
                Открыть карточку машины
              </button>
            )
          }
        >
          {selected && (
            <CarPreview
              car={selected}
              canEdit={canEditCar(selected)}
              onSaved={handleSaved}
              onActor={id => navigate(`/admin/users/${id}`)}
              dictionaries={{
                statuses: Object.fromEntries(STATUS_OPTIONS.filter(o => o.value).map(o => [o.value, o.label])),
                regions: selected.region ? { [selected.region.id]: selected.region.name } : {},
                bodyTypes: {},
              }}
            />
          )}
        </Drawer>
      </div>
    </div>
  )
}

function carWord(n) {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'машина'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'машины'
  return 'машин'
}

// ── Переключатель режима ─────────────────────────────────────────────────────

function ViewSwitch({ value, onChange }) {
  return (
    <div className="view-switch" role="group" aria-label="Режим просмотра">
      {[
        { key: 'table', icon: 'table', label: 'Таблица' },
        { key: 'map', icon: 'map', label: 'Карта' },
      ].map(v => (
        <button
          key={v.key}
          type="button"
          className={`view-switch__item ${value === v.key ? 'is-active' : ''}`.trim()}
          aria-pressed={value === v.key}
          title={v.label}
          onClick={() => onChange(v.key)}
        >
          <Icon name={v.icon} size={16} />
          <span className="view-switch__label">{v.label}</span>
        </button>
      ))}
    </div>
  )
}

// ── Ряд сигналов ─────────────────────────────────────────────────────────────

function AttentionBar({ counts, active, onToggle }) {
  // Сигнал без единой машины не показывается: пустой счётчик — это шум,
  // а не спокойствие. Активный остаётся видимым, иначе снять его будет нечем.
  const shown = SIGNALS.filter(s => counts[s.key] > 0 || active === s.key)
  if (shown.length === 0) return null

  return (
    <div className="attention">
      <span className="attention__label">Требует внимания</span>
      {shown.map(s => (
        <button
          key={s.key}
          type="button"
          className={`attention__chip attention__chip--${s.tone} ${
            active === s.key ? 'is-active' : ''
          }`.trim()}
          aria-pressed={active === s.key}
          onClick={() => onToggle(s.key)}
        >
          <Icon name={s.icon} size={14} />
          {s.label}
          <span className="attention__count">{counts[s.key] || 0}</span>
        </button>
      ))}
    </div>
  )
}

// ── Пагинация ────────────────────────────────────────────────────────────────

function Pagination({ meta, onChange }) {
  const { page = 1, pages = 1 } = meta || {}
  if (pages <= 1) return null

  return (
    <div className="pager">
      <button type="button" className="btn btn--ghost btn--sm"
        disabled={page <= 1} onClick={() => onChange(page - 1)}>
        <Icon name="chevronLeft" size={14} />
        Назад
      </button>
      <span className="pager__position">Страница {page} из {pages}</span>
      <button type="button" className="btn btn--ghost btn--sm"
        disabled={page >= pages} onClick={() => onChange(page + 1)}>
        Вперёд
        <Icon name="chevronRight" size={14} />
      </button>
    </div>
  )
}

// ── Фильтр региона (только для super-admin) ──────────────────────────────────

function RegionFilter({ value, onChange }) {
  const me = useAuthStore(s => s.user)
  const [regions, setRegions] = useState([])

  const isSuperAdmin = me?.role === 'admin' && (!me?.regions || me.regions.length === 0)

  useEffect(() => {
    if (isSuperAdmin) {
      regionsApi.list().then(r => setRegions(r.data?.data || [])).catch(() => {})
    }
  }, [isSuperAdmin])

  if (!isSuperAdmin || regions.length === 0) return null

  return (
    <label className="fleet__field">
      <span className="fleet__field-label">Регион</span>
      <select className="input" value={value} onChange={e => onChange(e.target.value)}>
        <option value="">Все регионы</option>
        {regions.map(r => (
          <option key={r.id} value={String(r.id)}>{r.name}</option>
        ))}
      </select>
    </label>
  )
}
